/**
 * Bundle 106 - Editable email templates (survey scope).
 *
 * Route: /admin/survey/email-templates (admin OR super_admin).
 *
 * Left sidebar lists all Bundle 106 templates; the right pane
 * is a live editor with Subject + HTML + variables list +
 * Preview button + Save draft / Publish / Version history.
 *
 * The preview iframes the server-rendered HTML with the sample
 * context so admins can eyeball the copy exactly as it will land.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Mail, Eye, Save, Send, History, RotateCcw } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const KEY_LABELS = {
  survey_attendee_invitation: 'Attendee invitation',
  survey_non_attendee_invitation: 'Non-attendee invitation',
  survey_reissue: 'Reissue apology',
  followup_14d: 'Follow-up 14 days',
  followup_60d: 'Follow-up 60 days',
};

const AdminEmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  // Bundle 108 - preview is now a modal (was an inline card the
  // user could not see below the tall editor).
  const [showPreview, setShowPreview] = useState(false);
  const [previewCtx, setPreviewCtx] = useState({
    first_name: 'Priya',
    event_name: 'Summer of Dosti - Dallas',
    survey_link: 'https://dostination.com/survey/token-abc123',
    signer_first_name: 'Vishal',
    signer_title: 'Founder',
    connection_name: 'Arjun',
  });
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/email-templates/v2');
      setTemplates(data.templates || []);
      if (!selectedKey && (data.templates || []).length) {
        setSelectedKey(data.templates[0].key);
      }
    } catch (e) {
      toast.error('Could not load templates.');
    }
  };

  useEffect(() => { load(); }, []);

  const current = useMemo(
    () => templates.find((t) => t.key === selectedKey),
    [templates, selectedKey],
  );

  useEffect(() => {
    if (!current) return;
    // Load draft first, else published copy.
    setSubject(current.draft_subject ?? current.published_subject ?? '');
    setHtml(current.draft_html ?? current.published_html ?? '');
    setDirty(false);
    setPreview(null);
  }, [current?.key]);

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/${selectedKey}/draft`,
        { subject, html });
      toast.success('Draft saved.');
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    if (!window.confirm(
      'Publish this template? Any new survey send will start using this copy.'
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/${selectedKey}/publish`,
        { subject, html });
      toast.success('Published.');
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed.');
    } finally { setBusy(false); }
  };

  const runPreview = async (ctx) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/email-templates/${selectedKey}/preview`,
        { subject, html, context: ctx || previewCtx },
      );
      setPreview(data);
      setShowPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed.');
    } finally { setBusy(false); }
  };

  const openVersions = async () => {
    try {
      const { data } = await api.get(
        `/admin/email-templates/${selectedKey}/versions`,
      );
      setVersions(data.versions || []);
      setShowVersions(true);
    } catch (e) {
      toast.error('Could not load version history.');
    }
  };

  const revertToVersion = async (idx) => {
    if (!window.confirm(
      'Revert to this version? Current published copy will move into history.'
    )) return;
    try {
      await api.post(`/admin/email-templates/${selectedKey}/revert`,
        { index: idx });
      toast.success('Reverted.');
      setShowVersions(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revert failed.');
    }
  };

  return (
    <div className="p-8 max-w-7xl space-y-6"
          data-testid="admin-email-templates-page">
      <div>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary-900" />
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Survey email templates
          </h1>
        </div>
        <p className="text-neutral-600 text-sm max-w-2xl mt-2">
          Edit the copy that goes out for every survey invitation,
          reissue, and follow-up. Drafts are safe; only published
          copy is used by real sends.
        </p>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <Card>
            <CardContent className="p-2 space-y-1">
              {templates.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    if (dirty && !window.confirm('Discard unsaved changes?')) return;
                    setSelectedKey(t.key);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                    selectedKey === t.key
                      ? 'bg-primary-900 text-white'
                      : 'hover:bg-neutral-100 text-primary-900'
                  }`}
                  data-testid={`template-item-${t.key}`}
                >
                  <div className="font-medium">
                    {KEY_LABELS[t.key] || t.key}
                  </div>
                  <div className="text-xs opacity-75 mt-0.5 flex gap-2">
                    {t.has_draft ? (
                      <span className="bg-yellow-100 text-yellow-900 px-1.5 rounded">
                        draft
                      </span>
                    ) : null}
                    {t.published ? (
                      <span className={selectedKey === t.key ? 'opacity-90' : 'text-success-700'}>
                        published
                      </span>
                    ) : (
                      <span className="text-neutral-500">unpublished</span>
                    )}
                    <span className="text-neutral-400">
                      v{t.version_count}
                    </span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="col-span-9 space-y-4">
          {current ? (
            <>
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-neutral-500">
                        Editing
                      </div>
                      <div className="font-display text-2xl font-bold text-primary-900">
                        {KEY_LABELS[current.key] || current.key}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={openVersions}
                                data-testid="template-versions-btn"
                                disabled={busy || !current.version_count}>
                        <History className="h-4 w-4 mr-2" />
                        History ({current.version_count})
                      </Button>
                      <Button variant="outline" onClick={() => runPreview()}
                                data-testid="template-preview-btn"
                                disabled={busy}>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button variant="outline" onClick={saveDraft}
                                data-testid="template-save-draft-btn"
                                disabled={busy || !dirty}>
                        <Save className="h-4 w-4 mr-2" />
                        Save draft
                      </Button>
                      <Button className="bg-primary-900 text-white hover:bg-primary-800"
                                onClick={publish}
                                data-testid="template-publish-btn"
                                disabled={busy}>
                        <Send className="h-4 w-4 mr-2" />
                        Publish
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-widest text-neutral-500">
                      Available variables
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(current.variables || []).map((v) => (
                        <Badge key={v} variant="outline"
                                data-testid={`template-var-${v}`}
                                className="font-mono">
                          {'{{ '}{v}{' }}'}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="tpl-subject" className="text-xs uppercase tracking-widest text-neutral-500">
                      Subject
                    </Label>
                    <Input id="tpl-subject" value={subject}
                            onChange={(e) => {
                              setSubject(e.target.value); setDirty(true);
                            }}
                            className="mt-1"
                            data-testid="template-subject-input" />
                  </div>

                  <div>
                    <Label htmlFor="tpl-html" className="text-xs uppercase tracking-widest text-neutral-500">
                      Body HTML
                    </Label>
                    <Textarea id="tpl-html" value={html}
                            onChange={(e) => {
                              setHtml(e.target.value); setDirty(true);
                            }}
                            rows={16}
                            className="mt-1 font-mono text-xs"
                            data-testid="template-html-textarea" />
                  </div>
                </CardContent>
              </Card>

            </>
          ) : (
            <div className="text-neutral-500 text-sm">
              Select a template.
            </div>
          )}
        </div>
      </div>

      <Dialog open={showPreview}
                onOpenChange={(v) => setShowPreview(v)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                        data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview email</DialogTitle>
          </DialogHeader>
          {preview ? (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Subject
                </div>
                <div className="font-semibold text-primary-900 mt-1"
                      data-testid="template-preview-subject">
                  {preview.rendered_subject}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Rendered HTML
                </div>
                <div className="mt-2 border border-neutral-200 rounded-md p-4 bg-white overflow-x-auto"
                      data-testid="template-preview-html"
                      dangerouslySetInnerHTML={{
                        __html: preview.rendered_html,
                      }} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                  Sample variables
                </div>
                <div className="grid grid-cols-2 gap-2"
                      data-testid="template-preview-variables">
                  {(preview.variables || []).map((v) => (
                    <div key={v} className="flex flex-col">
                      <label className="text-[10px] text-neutral-500"
                              htmlFor={`prev-var-${v}`}>{v}</label>
                      <input
                        id={`prev-var-${v}`}
                        value={previewCtx[v] || ''}
                        onChange={(e) => setPreviewCtx((p) => ({
                          ...p, [v]: e.target.value,
                        }))}
                        className="text-xs border border-neutral-200 rounded-md px-2 py-1"
                        data-testid={`template-preview-var-${v}`}
                      />
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline"
                          className="mt-2"
                          onClick={() => runPreview(previewCtx)}
                          disabled={busy}
                          data-testid="template-preview-rerender-btn">
                  Re-render with these values
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-neutral-500">Loading...</div>
          )}
          <DialogFooter>
            <Button variant="ghost"
                      onClick={() => setShowPreview(false)}
                      data-testid="template-preview-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showVersions}
                onOpenChange={(v) => setShowVersions(v)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"
                        data-testid="template-versions-dialog">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
          </DialogHeader>
          {versions.length === 0 ? (
            <div className="text-neutral-500 text-sm">
              No historic versions.
            </div>
          ) : (
            <div className="divide-y border border-neutral-200 rounded-md">
              {versions.map((v, i) => (
                <div key={i} className="p-3 flex items-start justify-between gap-4"
                      data-testid={`template-version-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-500">
                      {v.published_at
                        ? new Date(v.published_at).toLocaleString()
                        : 'undated'}
                    </div>
                    <div className="text-sm font-semibold text-primary-900 truncate">
                      {v.subject}
                    </div>
                  </div>
                  <Button size="sm" variant="outline"
                            onClick={() => revertToVersion(i)}
                            data-testid={`template-revert-btn-${i}`}>
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Revert
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost"
                      onClick={() => setShowVersions(false)}
                      data-testid="template-versions-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailTemplatesPage;
